import { useLocation } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Carousel } from '@factorialco/f0-react/dist/experimental'
import type { AllProductUpdatesQuery } from 'generated/resources/graphql'

type ProductUpdatesUpdate =
  AllProductUpdatesQuery['productUpdates']['updatesConnection']['edges'][number]['node']

import i18n from 'modules/core/lib/i18n'

import Buttons from 'modules/product_updates/components/Buttons'
import Media from 'modules/product_updates/components/ListSection/_Item/_Media'
import { useGetProductUpdates } from 'modules/product_updates/hooks/useGetProductUpdates'
import { getColorProductUpdates } from 'modules/product_updates/utils/getColorProductUpdates'

const HeroSection = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const rawUpsellOrigin = queryParams.get('upsell_origin')
  // Remove leading underscore if present
  const upsellOrigin = rawUpsellOrigin?.startsWith('_') ? rawUpsellOrigin.slice(1) : rawUpsellOrigin

  const { productUpdates } = useGetProductUpdates({
    featuredIn: 'hero_home',
  })

  const productUpdateMedia = (
    update: ProductUpdatesUpdate
  ): { alt: string; url: string; video?: boolean }[] => {
    return (
      update.media?.map((item) => {
        const imgObj = {
          url: item.video?.mp4Url || item.url || '',
          alt: '',
          video: !!item.video?.mp4Url || item.url?.includes('.mp4'),
        }
        return imgObj
      }) || []
    )
  }

  const handleMedia = (update: ProductUpdatesUpdate) => {
    const [video] = productUpdateMedia(update)
    return video
  }

  return (
    <Box
      style={{
        marginTop: '-20px',
        position: 'relative',
        background: `radial-gradient(50% 30% at 50% 0%, ${
          getColorProductUpdates(productUpdates[0]?.productModule?.urlIdentifier).gradientStart
        } 0%, rgb(255, 255, 255) 80%)`,
      }}
    >
      <Box paddingTop="s48" paddingBottom="s24">
        <Box gap="s8" width="s400">
          <span className="text-xl font-semibold">{i18n.t('product_updates.hero_home.title')}</span>
          <span className="text-lg font-normal text-f1-foreground-secondary">
            {i18n.t('product_updates.hero_home.subtitle')}
          </span>
        </Box>
      </Box>
      <Carousel
        columns={{
          default: 1,
        }}
        showArrows
        showDots
        autoplay
        delay={5000}
      >
        {productUpdates.map((update) => (
          <Box
            flexDirection="row"
            justifyContent="center"
            style={{
              padding: '32px 0',
            }}
            gap="s40"
            key={update?.id}
          >
            <Box
              className="aspect-video flex-grow rounded-xl shadow-md"
              style={{
                maxWidth: '756px',
              }}
            >
              <Media
                video={handleMedia(update) || undefined}
                thumbnail={update?.thumbnail}
                title={update?.title}
                autoPlay
              />
            </Box>

            <Box padding="s24" gap="s16" width="s400">
              <Box width="fit-content">
                <F0TagRaw text={update?.productModule?.name || '-'} />
              </Box>
              <Box gap="s8">
                <span className="text-2xl font-semibold">{update?.title}</span>
                <span className="text-lg font-normal text-f1-foreground-secondary">
                  {update?.shortDescription}
                </span>
              </Box>
              <Box gap="s8" flexDirection="row">
                <Buttons
                  chargebeeModuleName={update?.chargebeeModuleName}
                  productUpdateSlug={update?.slug}
                  isAlreadyReleased
                  productUpdateId={update?.id}
                  featureIdentifier={update?.featureRelated?.featureIdentifier || ''}
                  version="factorial-one"
                  navigationSlug={update?.navigationSlug || undefined}
                  source={`From product news hero ${update?.slug} ${
                    upsellOrigin ? ` - ${upsellOrigin}` : ''
                  }`}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Carousel>
    </Box>
  )
}

export default HeroSection
